import React, { useEffect, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import MainLayout from './layouts/MainLayout';
import ProfileSettingsPage from '@pages/ProfileSettingsPage.jsx';
// import CreatePatientPage from "@pages/CreatePatientPage.jsx";
import AnalyticsPage from '@pages/AnalyticsPage.jsx';
import PatientReportPage from '@components/patients/PatientReport.jsx';
import DocsPage from '@components/common/DocsPage.jsx';
import DocsNav from '@components/common/DocsNav.jsx';
import { EnforceChangePassword } from './components/enforceChangePassword/enforce-change-password';
import { onNotification, setupMessageListener } from './config/firebaseNotification';
import { ToastContainer } from 'react-toastify';
import { AppLocalEnum, getLanguage } from '@/translate/text';
import VideoCall from '@components/videoCall/VideoCall';
import NewNotificationPage from '@pages/NewNotificationPage';

// Lazy load pages for better performance
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const PatientsPage = React.lazy(() => import('./pages/PatientsPage'));
const PatientProfilePage = React.lazy(() => import('./pages/PatientProfilePage'));
const TicketsPage = React.lazy(() => import('./pages/TicketsPage'));
const TicketDetailsPage = React.lazy(() => import('./pages/TicketDetailsPage'));
const PublicTicketDetailsPage = React.lazy(() => import('./pages/PublicTicketDetailsPage'));
const NewTicketPage = React.lazy(() => import('./pages/NewTicketPage'));
const AdminDashboardPage = React.lazy(() => import('./pages/AdminDashboardPage'));
const CalendarPage = React.lazy(() => import('./pages/Calendar'));
const SendCodePage = React.lazy(() => import('./pages/SendCode'));
const ForgetPassword = React.lazy(() => import('./pages/ForgetPassword'));
const PlanDetailsPage = React.lazy(() => import('./pages/PlanDetailsPage'));
const ChatbotTicketDetailsPage = React.lazy(() => import('./pages/ChatbotTicketDetailsPage'));
const ChatbotTicketsPage = React.lazy(() => import('./pages/ChatbotTicketsPage'));
const NotificationCenterPage = React.lazy(() => import('./pages/NotificationCenterPage'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="flex flex-col items-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      <span className="mt-4 text-gray-600">Loading...</span>
    </div>
  </div>
);

function App() {
  const navigate = useNavigate();
  const getDirection = () =>
    getLanguage(localStorage.getItem('language')) === AppLocalEnum.AR ? 'rtl' : 'ltr';
  const direction = getDirection();

  return (
    <>
      <div style={{ direction }} className="app">
        {/* <ToastContainer /> */}
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<LoginPage />} />
            <Route path="/activate-account/:id" element={<EnforceChangePassword />} />
            <Route path="sendcode" element={<SendCodePage />} />
            <Route path="forgetpassword" element={<ForgetPassword />} />

            {/* <Route path="/home" element={<MainLayout />}> */}

            <Route path="/" element={<MainLayout />}>
              {/* <Route index element={<Navigate to="/dashboard" replace />} /> */}

              <Route
                path="dashboard"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <DashboardPage />
                  </Suspense>
                }
              />

              {/* Public Ticket View Route
        <Route
          path="/public/tickets/:id"
          element={
            <PublicTicketDetailsPage />
          }
        /> */}

              {/* <Route index element={<Navigate to="/dashboard" replace />} /> */}
              {/* <Route
            path="patients/new"
            element={
              <CreatePatientPage />
            }
          /> */}

              <Route path="patients/:id/report" element={<PatientReportPage />} />
              <Route path="settings/profile" element={<ProfileSettingsPage />} />
              <Route path="calendar" element={<CalendarPage />} />

              <Route path="patients" element={<PatientsPage />} />

              <Route path="patients/:id" element={<PatientProfilePage />} />
              <Route path="/patients/:id/plans" element={<PlanDetailsPage />} />

              <Route path="appointments" element={<TicketsPage />} />

              <Route path="appointments/new" element={<NewTicketPage />} />

              <Route path="appointments/:id" element={<TicketDetailsPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="admin" element={<AdminDashboardPage />} />
              <Route path="video" element={<VideoCall />} />
              <Route path="chatbot-ticket-details" element={<ChatbotTicketDetailsPage />} />
              <Route path="chatbot-tickets" element={<ChatbotTicketsPage />} />
              <Route path="notification-center" element={<NotificationCenterPage />} />
              <Route path="notification-center/new" element={<NewNotificationPage />} />

              {/* Error Routes */}
              <Route
                path="unauthorized"
                element={
                  <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="text-center">
                      <h1 className="text-4xl font-bold text-gray-900 mb-4">Access Denied</h1>
                      <p className="text-gray-600 mb-8">
                        You don't have permission to access this page.
                      </p>
                      <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
                      >
                        Go Back
                      </button>
                    </div>
                  </div>
                }
              />
              <Route path="docs/:docId" element={<DocsPage />} />
              <Route path="docs" element={<DocsNav />} />
              <Route
                path="*"
                element={
                  <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="text-center">
                      <h1 className="text-4xl font-bold text-gray-900 mb-4">
                        404 - Page Not Found
                      </h1>
                      <p className="text-gray-600 mb-8">
                        The page you're looking for doesn't exist.
                      </p>
                      <button
                        onClick={() => navigate('/')}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
                      >
                        Go Home
                      </button>
                    </div>
                  </div>
                }
              />
            </Route>
          </Routes>
        </Suspense>
      </div>
    </>
  );
}

export default App;
